import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { getLocalStorage } from '../utils';
import { REHYDRATE } from 'redux-persist';
import type { Action } from '@reduxjs/toolkit';
import { RootState } from './store';
import { BASE_API_URL } from '../config';
import { AUTH_TOKEN, SESSION_ID } from '../data/local-storage-constants';
import { toastError, toastSuccess } from '../utils/react-toaster';
import { handleLogout } from '../helpers/common-helper';

type extraOptionsProps = {
  showSuccessMessage?: boolean;
  showErrorMessage?: boolean;
};

function isHydrateAction(action: Action): action is Action<typeof REHYDRATE> & {
  key: string;
  payload: RootState;
  err: unknown;
} {
  return action.type === REHYDRATE;
}

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_API_URL || '',
  prepareHeaders: (headers, ...rest) => {
    console.log(rest);
    const token = getLocalStorage(AUTH_TOKEN);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    const sessionId = getLocalStorage(SESSION_ID);
    if (sessionId) {
      headers.set('session_id', sessionId);
    }
    return headers;
  },
});

const baseQueryWithInterceptor: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args: any, api: any, extraOptions: extraOptionsProps = {}) => {
  const result: any = await baseQuery(args, api, extraOptions);

  if (extraOptions?.showSuccessMessage && !result?.error) {
    toastSuccess(result.data?.message);
  }

  if (extraOptions.showErrorMessage && result?.error) {
    toastError(result.error.data?.message);
  }

  if (result?.error?.status === 401) {
    handleLogout(api.dispatch);
  }
  return result;
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithInterceptor,
  /**
   * This api has endpoints injected in adjacent files,
   * which is why no endpoints are shown below.
   * If you want all endpoints defined in the same file, they could be included here instead
   */
  endpoints: () => ({}),
  // to prevent circular type issues, the return type needs to be annotated as any
  // This is used to handle all the subscription on page refresh
  extractRehydrationInfo(action): any {
    if (isHydrateAction(action)) {
      // when persisting the api reducer
      if (action.key === 'key used with redux-persist') {
        return action.payload;
      }

      // When persisting the root reducer
      return action?.payload?.[baseApi.reducerPath];
    }
  },
});
